import { apiService } from './api';
import { PaginationInfo } from '../types';

// ─── Denominations ────────────────────────────────────────────────────────────

/** Ordered list of standard cash denominations (highest → lowest). */
export const CASH_DENOMINATIONS = [5000, 2000, 1000, 500, 100, 50, 20, 10, 5, 2, 1];

// ─── Types ─────────────────────────────────────────────────────────────────────

export type TillSessionStatus = 'Open' | 'Closed';

export interface DenominationEntry {
  denomination: number;
  count: number;
  subtotal: number;
}

export interface TillSessionListDTO {
  id: number;
  sessionNumber: string;
  cashierId: number;
  cashierName: string;
  openedAt: string;
  closedAt?: string;
  status: TillSessionStatus;
  openingBalance: number;
  expectedClosingBalance?: number;
  declaredClosingBalance?: number;
  variance?: number;
  notes?: string;
}

export interface TillSessionDTO extends TillSessionListDTO {
  openingDenominations: DenominationEntry[];
  closingDenominations?: DenominationEntry[];
  /** Sum of all Cash payments recorded against this session (live). */
  currentCashTotal: number;
  /** Opening balance + currentCashTotal — the live expected balance right now. */
  currentExpectedBalance: number;
}

export interface TillStatusDTO {
  hasActiveTill: boolean;
  session?: TillSessionDTO;
}

export interface OpenTillSessionRequest {
  openingDenominations: DenominationEntry[];
  notes?: string;
}

export interface CloseTillSessionRequest {
  closingDenominations: DenominationEntry[];
  notes?: string;
}

export interface TillHistoryParams {
  pageNumber?: number;
  pageSize?: number;
  cashierId?: number;
  from?: string;
  to?: string;
}

export interface TillHistoryResponse {
  data: TillSessionListDTO[];
  success: boolean;
  pagination: PaginationInfo;
}

// ─── Service ───────────────────────────────────────────────────────────────────

const TILL_API = '/till-sessions';

export const tillSessionService = {
  /** Check whether the current user has an open till session. */
  getMyActive: () =>
    apiService.get<TillStatusDTO>(`${TILL_API}/my-active`),

  /** Open a new till session with opening denomination breakdown. */
  openSession: (data: OpenTillSessionRequest) =>
    apiService.post<TillSessionDTO>(`${TILL_API}/open`, data),

  /** Close the current user's open till session with closing denomination breakdown. */
  closeSession: (data: CloseTillSessionRequest) =>
    apiService.post<TillSessionDTO>(`${TILL_API}/close`, data),

  /** Retrieve paginated till session history with optional filters. */
  getHistory: (params?: TillHistoryParams) =>
    apiService.get<TillHistoryResponse>(
      TILL_API,
      params as Record<string, unknown>
    ),

  /** Retrieve a single till session by ID. */
  getSessionById: (id: number | string) =>
    apiService.get<TillSessionDTO>(`${TILL_API}/${id}`),
};
